/**
 *
 * SaveAssessmentCard
 *
 */

import React from 'react';
import _get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import {
  makeSelectAssessment,
  makeSelectAssessmentResult,
} from 'containers/Assessment/selectors';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import Typography from '@material-ui/core/Typography';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { normalizeResult } from 'components/SurveyResult/utils';

const useStyles = makeStyles(theme => ({
  cardContainer: {
    width: '100%',
    border: '1px solid #E9ECED',
    marginTop: '0.5rem',
    marginBottom: '0.5rem',
  },
  cardImage: {
    width: 100,
    [theme.breakpoints.down('xs')]: {
      width: 70,
    },
  },
  cardContent: {
    maxHeight: '4.12125rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  infoWrapper: {
    padding: '8px 8px 0 16px',
    maxHeight: 100,
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      padding: 8,
      display: 'flex',
      alignItems: 'center',
    },
  },
}));

const stateSelector = createStructuredSelector({
  assessment: makeSelectAssessment(),
  assessmentResult: makeSelectAssessmentResult(),
});

const SaveAssessmentCard = () => {
  const classes = useStyles();
  const { assessment, assessmentResult } = useSelector(stateSelector);
  const [siteCopy] = useSiteCopySelector(['authentication-modal']);

  let text;
  const prefix =
    _get(assessment, 'items.0.fields.globalOptions.signUpReminderLabel') ||
    _get(siteCopy, ['pageCopy', 'move-assessments-card-label'], '');
  if (_get(assessmentResult, 'scoringsObject.overallLabel')) {
    if (
      _get(assessment, 'items.0.fields.globalOptions.signUpReminderStyle') ===
      'score'
    ) {
      const { normalizedValue, normalizedMaxValue } = normalizeResult(
        _get(assessmentResult, 'scoringsObject.overallScoring'),
        _get(
          assessment,
          'items.0.fields.scoreComponents.overallScore.minValue',
        ),
        _get(
          assessment,
          'items.0.fields.scoreComponents.overallScore.maxValue',
        ),
        null,
        _get(assessment, 'items.0.fields.scoreComponents.overallScore.scale'),
      );
      text = prefix
        .replace('#score', Math.round(normalizedValue))
        .replace('#maxValue', Math.round(normalizedMaxValue));
    } else {
      text = prefix
        .concat(
          _get(assessmentResult, 'scoringsObject.overallLabel').toLowerCase(),
        )
        .concat('.');
    }
  } else {
    const availableIndexes = _get(assessmentResult, 'results.items', [])
      .map((item, index) => item && index)
      .filter(item => !!item);
    const labels = _get(assessmentResult, 'scoringsObject.labels', [])
      .map((item, index) => availableIndexes.includes(index) && item)
      .filter(item => !!item)
      .map(item => item.toLowerCase().replace('!', ''));
    if (labels.length > 2) {
      text = prefix
        .concat(labels.slice(0, 1).join(','))
        .concat(` ${_get(siteCopy, 'pageCopy.andMoreLabel')}.`);
    } else {
      text = prefix
        .concat(
          labels
            .slice(0, labels.length - 1)
            .join(',')
            .concat(` ${_get(siteCopy, 'pageCopy.andLabel')} `)
            .concat(labels[labels.length - 1]),
        )
        .concat('.');
    }
  }

  return (
    <Grid item xs={12} className={classes.cardContainer}>
      <Grid container>
        <Hidden xsDown>
          <Grid item>
            <img
              className={classes.cardImage}
              src={_get(assessment, 'items.0.fields.image.fields.file.url')}
              alt={_get(assessment, 'items.0.fields.name', '')}
            />
          </Grid>
          <Grid item xs className={classes.infoWrapper}>
            <Typography variant="h6" color="primary" gutterBottom>
              {_get(assessment, 'items.0.fields.name', '')}
            </Typography>
            {text && (
              <div className={classes.cardContent}>
                <Typography variant="overline" color="primaryText">
                  {text}
                </Typography>
              </div>
            )}
          </Grid>
        </Hidden>
        <Hidden smUp>
          <Grid item xs={12}>
            <Grid container>
              <Grid item>
                <img
                  className={classes.cardImage}
                  src={_get(assessment, 'items.0.fields.image.fields.file.url')}
                  alt={_get(assessment, 'items.0.fields.name', '')}
                />
              </Grid>
              <Grid item xs className={classes.infoWrapper}>
                <Typography variant="h6" color="primary">
                  {_get(assessment, 'items.0.fields.name', '')}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          {text && (
            <Grid item xs={12} className={classes.infoWrapper}>
              <div className={classes.cardContent}>
                <Typography variant="overline" color="primaryText">
                  {text}
                </Typography>
              </div>
            </Grid>
          )}
        </Hidden>
      </Grid>
    </Grid>
  );
};

export default SaveAssessmentCard;
